import { JuloUtils } from "../common/JuloUtils"
import { TranslationKey, Translator } from "../i18n/translation-key"

export enum ServiceAction {
  CHANGE = "CHANGE", // Byt
  ADJUST = "ADJUST", // Justera
  BLEED = "BLEED", // Lufta
  TRUE = "TRUE", // Rikta
  NEXUS_DIP = "NEXUS_DIP", // Nexus doppservice
  MOUNT_TUBELESS = "MOUNT_TUBELESS", // Montera tubeless
  MOUNT_TUBE = "MOUNT_TUBE", // Montera tubdäck
  UPDATE = "UPDATE", // Uppdatera
  TROUBLESHOOT = "TROUBLESHOOT", // Felsök
  DO_NOTHING = "DO_NOTHING", // Gör inget
  MOUNT = "MOUNT", // Montera
  BASIC_SERVICE = "BASIC_SERVICE", // Grundservice
  FULL_SERVICE = "FULL_SERVICE", // Fullservice
  CLEAN = "CLEAN", // Rengör
  LUBRICATE = "LUBRICATE", // Smörj
  WAX = "WAX", // Valla
  ACTIVE_WAX = "ACTIVE_WAX", // Aktiv valla
  STONE_GRIND = "STONE_GRIND", // Stenslipning
  BASE_REPAIR = "BASE_REPAIR", // Sårfyllning
  SCRAPE = "SCRAPE", // Sickla
  EDGE_SHARPEN = "EDGE_SHARPEN", // Slipa (kanter)
  EDGE_DETUNE = "EDGE_DETUNE", // Hänga (kanter)
  BLADE_SHARPEN = "BLADE_SHARPEN", // Slipning (skridskor)
  CHECK = "CHECK", // Kontrollera
  CHANGE_WHEEL_BEARINGS = "CHANGE_WHEEL_BEARINGS", // Byt lager
  CHANGE_BATTERIES = "CHANGE_BATTERIES", // Byt batterier
}

/** Actions that only apply to winter sports equipment */
export const WINTER_SPORTS_ACTIONS: ServiceAction[] = [
  ServiceAction.WAX,
  ServiceAction.ACTIVE_WAX,
  ServiceAction.STONE_GRIND,
  ServiceAction.BASE_REPAIR,
  ServiceAction.SCRAPE,
  ServiceAction.EDGE_SHARPEN,
  ServiceAction.EDGE_DETUNE,
  ServiceAction.BLADE_SHARPEN,
]

const SERVICE_ACTION_IMPERATIVE_KEYS: Record<ServiceAction, TranslationKey> = {
  [ServiceAction.CHANGE]: "common.serviceAction.change.imperative",
  [ServiceAction.ADJUST]: "common.serviceAction.adjust.imperative",
  [ServiceAction.TROUBLESHOOT]: "common.serviceAction.troubleshoot.imperative",
  [ServiceAction.DO_NOTHING]: "common.serviceAction.doNothing.imperative",
  [ServiceAction.BLEED]: "common.serviceAction.bleed.imperative",
  [ServiceAction.TRUE]: "common.serviceAction.true.imperative",
  [ServiceAction.MOUNT_TUBELESS]:
    "common.serviceAction.mountTubeless.imperative",
  [ServiceAction.MOUNT_TUBE]: "common.serviceAction.mountTube.imperative",
  [ServiceAction.NEXUS_DIP]: "common.serviceAction.nexusDip.imperative",
  [ServiceAction.UPDATE]: "common.serviceAction.update.imperative",
  [ServiceAction.MOUNT]: "common.serviceAction.mount.imperative",
  [ServiceAction.BASIC_SERVICE]: "common.serviceAction.basicService.imperative",
  [ServiceAction.FULL_SERVICE]: "common.serviceAction.fullService.imperative",
  [ServiceAction.CLEAN]: "common.serviceAction.clean.imperative",
  [ServiceAction.LUBRICATE]: "common.serviceAction.lubricate.imperative",
  [ServiceAction.WAX]: "common.serviceAction.wax.imperative",
  [ServiceAction.ACTIVE_WAX]: "common.serviceAction.activeWax.imperative",
  [ServiceAction.STONE_GRIND]: "common.serviceAction.stoneGrind.imperative",
  [ServiceAction.BASE_REPAIR]: "common.serviceAction.baseRepair.imperative",
  [ServiceAction.SCRAPE]: "common.serviceAction.scrape.imperative",
  [ServiceAction.EDGE_SHARPEN]: "common.serviceAction.edgeSharpen.imperative",
  [ServiceAction.EDGE_DETUNE]: "common.serviceAction.edgeDetune.imperative",
  [ServiceAction.BLADE_SHARPEN]: "common.serviceAction.bladeSharpen.imperative",
  [ServiceAction.CHECK]: "common.serviceAction.check.imperative",
  [ServiceAction.CHANGE_WHEEL_BEARINGS]:
    "common.serviceAction.changeWheelBearings.imperative",
  [ServiceAction.CHANGE_BATTERIES]:
    "common.serviceAction.changeBatteries.imperative",
}

const SERVICE_ACTION_PAST_PASSIVE_KEYS: Record<ServiceAction, TranslationKey> =
  {
    [ServiceAction.CHANGE]: "common.serviceAction.change.pastPassive",
    [ServiceAction.ADJUST]: "common.serviceAction.adjust.pastPassive",
    [ServiceAction.TROUBLESHOOT]:
      "common.serviceAction.troubleshoot.pastPassive",
    [ServiceAction.DO_NOTHING]: "common.serviceAction.doNothing.pastPassive",
    [ServiceAction.BLEED]: "common.serviceAction.bleed.pastPassive",
    [ServiceAction.TRUE]: "common.serviceAction.true.pastPassive",
    [ServiceAction.MOUNT_TUBELESS]:
      "common.serviceAction.mountTubeless.pastPassive",
    [ServiceAction.MOUNT_TUBE]: "common.serviceAction.mountTube.pastPassive",
    [ServiceAction.NEXUS_DIP]: "common.serviceAction.nexusDip.pastPassive",
    [ServiceAction.UPDATE]: "common.serviceAction.update.pastPassive",
    [ServiceAction.MOUNT]: "common.serviceAction.mount.pastPassive",
    [ServiceAction.BASIC_SERVICE]:
      "common.serviceAction.basicService.pastPassive",
    [ServiceAction.FULL_SERVICE]:
      "common.serviceAction.fullService.pastPassive",
    [ServiceAction.CLEAN]: "common.serviceAction.clean.pastPassive",
    [ServiceAction.LUBRICATE]: "common.serviceAction.lubricate.pastPassive",
    [ServiceAction.WAX]: "common.serviceAction.wax.pastPassive",
    [ServiceAction.ACTIVE_WAX]: "common.serviceAction.activeWax.pastPassive",
    [ServiceAction.STONE_GRIND]: "common.serviceAction.stoneGrind.pastPassive",
    [ServiceAction.BASE_REPAIR]: "common.serviceAction.baseRepair.pastPassive",
    [ServiceAction.SCRAPE]: "common.serviceAction.scrape.pastPassive",
    [ServiceAction.EDGE_SHARPEN]:
      "common.serviceAction.edgeSharpen.pastPassive",
    [ServiceAction.EDGE_DETUNE]: "common.serviceAction.edgeDetune.pastPassive",
    [ServiceAction.BLADE_SHARPEN]:
      "common.serviceAction.bladeSharpen.pastPassive",
    [ServiceAction.CHECK]: "common.serviceAction.check.pastPassive",
    [ServiceAction.CHANGE_WHEEL_BEARINGS]:
      "common.serviceAction.changeWheelBearings.pastPassive",
    [ServiceAction.CHANGE_BATTERIES]:
      "common.serviceAction.changeBatteries.pastPassive",
  }

const SERVICE_ACTION_FUTURE_PASSIVE_KEYS: Record<
  ServiceAction,
  TranslationKey
> = {
  [ServiceAction.CHANGE]: "common.serviceAction.change.futurePassive",
  [ServiceAction.ADJUST]: "common.serviceAction.adjust.futurePassive",
  [ServiceAction.TROUBLESHOOT]:
    "common.serviceAction.troubleshoot.futurePassive",
  [ServiceAction.DO_NOTHING]: "common.serviceAction.doNothing.futurePassive",
  [ServiceAction.BLEED]: "common.serviceAction.bleed.futurePassive",
  [ServiceAction.TRUE]: "common.serviceAction.true.futurePassive",
  [ServiceAction.MOUNT_TUBELESS]:
    "common.serviceAction.mountTubeless.futurePassive",
  [ServiceAction.MOUNT_TUBE]: "common.serviceAction.mountTube.futurePassive",
  [ServiceAction.NEXUS_DIP]: "common.serviceAction.nexusDip.futurePassive",
  [ServiceAction.UPDATE]: "common.serviceAction.update.futurePassive",
  [ServiceAction.MOUNT]: "common.serviceAction.mount.futurePassive",
  [ServiceAction.BASIC_SERVICE]:
    "common.serviceAction.basicService.futurePassive",
  [ServiceAction.FULL_SERVICE]:
    "common.serviceAction.fullService.futurePassive",
  [ServiceAction.CLEAN]: "common.serviceAction.clean.futurePassive",
  [ServiceAction.LUBRICATE]: "common.serviceAction.lubricate.futurePassive",
  [ServiceAction.WAX]: "common.serviceAction.wax.futurePassive",
  [ServiceAction.ACTIVE_WAX]: "common.serviceAction.activeWax.futurePassive",
  [ServiceAction.STONE_GRIND]: "common.serviceAction.stoneGrind.futurePassive",
  [ServiceAction.BASE_REPAIR]: "common.serviceAction.baseRepair.futurePassive",
  [ServiceAction.SCRAPE]: "common.serviceAction.scrape.futurePassive",
  [ServiceAction.EDGE_SHARPEN]:
    "common.serviceAction.edgeSharpen.futurePassive",
  [ServiceAction.EDGE_DETUNE]: "common.serviceAction.edgeDetune.futurePassive",
  [ServiceAction.BLADE_SHARPEN]:
    "common.serviceAction.bladeSharpen.futurePassive",
  [ServiceAction.CHECK]: "common.serviceAction.check.futurePassive",
  [ServiceAction.CHANGE_WHEEL_BEARINGS]:
    "common.serviceAction.changeWheelBearings.futurePassive",
  [ServiceAction.CHANGE_BATTERIES]:
    "common.serviceAction.changeBatteries.futurePassive",
}

const SERVICE_ACTION_NOUN_KEYS: Record<ServiceAction, TranslationKey> = {
  [ServiceAction.CHANGE]: "common.serviceAction.change.noun",
  [ServiceAction.ADJUST]: "common.serviceAction.adjust.noun",
  [ServiceAction.TROUBLESHOOT]: "common.serviceAction.troubleshoot.noun",
  [ServiceAction.DO_NOTHING]: "common.serviceAction.doNothing.noun",
  [ServiceAction.BLEED]: "common.serviceAction.bleed.noun",
  [ServiceAction.TRUE]: "common.serviceAction.true.noun",
  [ServiceAction.MOUNT_TUBELESS]: "common.serviceAction.mountTubeless.noun",
  [ServiceAction.MOUNT_TUBE]: "common.serviceAction.mountTube.noun",
  [ServiceAction.NEXUS_DIP]: "common.serviceAction.nexusDip.noun",
  [ServiceAction.UPDATE]: "common.serviceAction.update.noun",
  [ServiceAction.MOUNT]: "common.serviceAction.mount.noun",
  [ServiceAction.BASIC_SERVICE]: "common.serviceAction.basicService.noun",
  [ServiceAction.FULL_SERVICE]: "common.serviceAction.fullService.noun",
  [ServiceAction.CLEAN]: "common.serviceAction.clean.noun",
  [ServiceAction.LUBRICATE]: "common.serviceAction.lubricate.noun",
  [ServiceAction.WAX]: "common.serviceAction.wax.noun",
  [ServiceAction.ACTIVE_WAX]: "common.serviceAction.activeWax.noun",
  [ServiceAction.STONE_GRIND]: "common.serviceAction.stoneGrind.noun",
  [ServiceAction.BASE_REPAIR]: "common.serviceAction.baseRepair.noun",
  [ServiceAction.SCRAPE]: "common.serviceAction.scrape.noun",
  [ServiceAction.EDGE_SHARPEN]: "common.serviceAction.edgeSharpen.noun",
  [ServiceAction.EDGE_DETUNE]: "common.serviceAction.edgeDetune.noun",
  [ServiceAction.BLADE_SHARPEN]: "common.serviceAction.bladeSharpen.noun",
  [ServiceAction.CHECK]: "common.serviceAction.check.noun",
  [ServiceAction.CHANGE_WHEEL_BEARINGS]:
    "common.serviceAction.changeWheelBearings.noun",
  [ServiceAction.CHANGE_BATTERIES]: "common.serviceAction.changeBatteries.noun",
}

type PartSettingsList = Array<{ action: ServiceAction; price: number }>
type PartSettingsMap = Record<ServiceAction, number>

export class ServicePartActionUtils {
  static selectAction(actions: ServiceAction[]): ServiceAction {
    const firstAction = actions[0]
    if (!firstAction) {
      throw new Error("Cannot select actions from empty list.")
    }

    if (actions.includes(ServiceAction.ADJUST)) {
      return ServiceAction.ADJUST
    }
    if (actions.includes(ServiceAction.CHANGE)) {
      return ServiceAction.CHANGE
    }

    return firstAction
  }

  private static actionsToLabelHelper(
    actions: ServiceAction[],
    t: Translator,
  ): string {
    const labels = actions.map((action) =>
      t(ServicePartActionUtils.toLabelKey(action)),
    )
    const last = labels[labels.length - 1]
    if (last === undefined) return ""
    if (labels.length === 1) return last

    return t("common.serviceAction.alternatives", {
      first: labels.slice(0, -1).join(", "),
      last,
    })
  }

  static actionsToLabel(actions: ServiceAction[], t: Translator): string {
    const label = ServicePartActionUtils.actionsToLabelHelper(actions, t)
    return JuloUtils.capitalize(label)
  }

  /**
   * True when the action installs a component the bike does not have yet.
   * Nothing about such a part can be graded — there is no component on the bike
   * to inspect — so a wear grade on it describes something that is not there.
   */
  static addsNewComponent(action: ServiceAction): boolean {
    return action === ServiceAction.MOUNT
  }

  static toLabelKey(action: ServiceAction): TranslationKey {
    return SERVICE_ACTION_IMPERATIVE_KEYS[action]
  }

  static toPastPassiveKey(action: ServiceAction): TranslationKey {
    return SERVICE_ACTION_PAST_PASSIVE_KEYS[action]
  }

  static toFuturePassiveKey(action: ServiceAction): TranslationKey {
    return SERVICE_ACTION_FUTURE_PASSIVE_KEYS[action]
  }

  static toNounKey(action: ServiceAction): TranslationKey {
    return SERVICE_ACTION_NOUN_KEYS[action]
  }

  static getLaborPricesMap(pricesList: PartSettingsList): PartSettingsMap {
    const laborPrices = {} as PartSettingsMap

    for (const { price, action } of pricesList) {
      laborPrices[action] = price
    }

    for (const action of Object.keys(ServiceAction) as ServiceAction[]) {
      if (laborPrices[action] === undefined) laborPrices[action] = 0
    }

    return laborPrices
  }

  static getLaborPricesList(pricesMap: PartSettingsMap): PartSettingsList {
    return Object.entries(pricesMap).map(([action, price]) => ({
      action: action as ServiceAction,
      price,
    }))
  }

  static createEmptyPricesMap(): PartSettingsMap {
    const map = {} as PartSettingsMap
    for (const action of Object.values(ServiceAction)) {
      map[action] = 0
    }
    return map
  }

  static createEmptyLockedMap(): Record<ServiceAction, boolean> {
    const map = {} as Record<ServiceAction, boolean>
    for (const action of Object.values(ServiceAction)) {
      map[action] = false
    }
    return map
  }
}
